import './polyfills'
import {
  defaultShouldDehydrateQuery,
  isServer,
  MutationCache,
  QueryCache,
  QueryClient,
  type Query,
} from '@tanstack/react-query'
import { basename } from './lib/utils'
import { correlationHeaders } from './lib/client-trace'
import { timedFetch } from './lib/fetch-timing'
import { documentOwnsPersistedCache, withSdkParams } from './lib/sdk-params'
import { resolveRedirectTarget } from './lib/redirect-target'
import { persistQueryClient, persistQueryClientSave, removeOldestQuery } from '@tanstack/react-query-persist-client'
import {
  documentRenderedAt,
  isPersistable,
  supersededDocumentQueries,
  withoutDocumentQueries,
} from './lib/query-persistence'
import { fetchableByDefault } from './lib/session-query'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  splitLink,
  TRPCClientError,
  type HTTPHeaders,
  type TRPCLink,
} from '@trpc/client'
import type { AppRouter } from 'server/trpc'
import type { inferRouterOutputs } from '@trpc/server'
import { trpcSSRCallFn } from './lib/trpc-server-fn'
import { shouldRetryQuery } from './lib/query-retry'
import { revalidateAfterWrite } from './lib/revalidate'
import { claimFlightSeeded, createFlightFetch, flightHeaders } from './lib/single-flight'
import { useAppStore } from './lib/app-store'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { observable } from '@trpc/server/observable'
import { toast } from './lib/toast'
import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import type { ErrorCode } from 'shared'
import {
  classifyTrpcErrorToast,
  persistedCacheIdentity,
  pickIncompleteTestPath,
  surfacesQueryError,
} from './main-helpers'

/**
 * Shared client singletons (query client, tRPC client/proxy, persister). Import-safe on the server, which renders
 * the shell: no top-level `window` / `localStorage` access.
 */

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /**
       * Opt OUT of the global post-mutation revalidation (see the MutationCache below). Reserve it for writes
       * that provably cannot change anything the user could be looking at AND fire often enough that a full
       * revalidation per call would be pathological (the test's per-question autosave and its heartbeat).
       */
      invalidates?: false
    }
    queryMeta: {
      /** A failed refresh of data already shown keeps it and says nothing (`surfacesQueryError`). */
      quietRefetch?: true
    }
  }
}

// Factory so the server can mint a fresh client per request: a module singleton would leak one request's
// dehydrated cache into the next on the long-lived Bun server.
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    // On the caches, NOT a tRPC link, so they can be OBSERVER-GATED: a hover-preload prefetches the target route's
    // loader queries, and an error there has no observer, so a toast or redirect would fire "from nowhere".
    // Mutations have no render boundary, so they always surface.
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (surfacesQueryError(query)) handleClientTrpcError(error)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        // A failed write revalidates nothing, so nothing may claim what a flight seeded before it.
        claimFlightSeeded()
        handleClientTrpcError(error)
      },
      // EVERY successful mutation revalidates the WHOLE cache (policy: lib/revalidate) — no per-mutation invalidation
      // lists to keep in sync with the backend's write fan-out. Fire-and-forget, and it runs BEFORE the mutation's own
      // callbacks: a call site that must hold its UI awaits `revalidateAfterWrite` itself in `onSuccess`.
      // Unrelated visible queries refetch too, deliberately; never narrow it to targeted keys: see lib/revalidate.
      onSuccess: (_data, _variables, _onMutateResult, mutation, context) => {
        if (mutation.meta?.invalidates === false) return
        // What the mutation's own response already refreshed (lib/single-flight) is skipped, not refetched.
        const fresh = claimFlightSeeded()
        void revalidateAfterWrite(context.client, fresh ? (query) => !fresh.has(query.queryHash) : undefined)
      },
    }),
    defaultOptions: {
      queries: {
        // Client-only (lib/query-retry): on a lossy link a single dropped request would fail a query permanently. SSR
        // keeps no-retry so a render never blocks on backoff; mutations are untouched (a retry could double-submit).
        retry: isServer ? false : shouldRetryQuery,
        // 0.5s → 1s → 2s, so a genuinely-down backend surfaces its error within seconds instead of hanging the UI.
        retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 4000),
        staleTime: 60 * 1000,
        // Bounded, not Infinity: report/team queries are keyed on the selected member subset, so browsing
        // members spawns a cache entry per combination — uncollected, those accumulated and slowed the app.
        gcTime: 5 * 60 * 1000,
        // NO `placeholderData: keepPreviousData` as a global default. It is PER-QUERY, so a screen reading several
        // queries tears, and it LIES to any consumer that does not check `isPlaceholderData` (three dialogs needed an
        // `id === props.id` guard). Opt in with `useDeferredValue`, which holds the whole committed tree instead.
      },
    },
  })
}

export const queryClient = makeQueryClient()

// Picks the first unfinished questionnaire so a TEST_INCOMPLETE redirect resumes where the user left off.
export function firstIncompleteTestPath(): string {
  const user = queryClient.getQueryData<{
    testPassed?: boolean | null
    testPassedValues?: boolean | null
    testPassedPersonality?: boolean | null
  } | null>(trpc.auth.getUser.queryKey())
  return pickIncompleteTestPath(user)
}

// Backend control-flow errors arrive as a stable machine code in the tRPC `message`; the .po catalogs own the
// wording. Keep this map in sync with `ErrorCode`.
const ERROR_TRANSLATIONS: Partial<Record<ErrorCode, MessageDescriptor>> = {
  INVALID_CREDENTIALS: msg`Invalid email or password`,
  NOT_YOUR_EMPLOYER: msg`This is not one of your employers`,
  INSUFFICIENT_PERMISSIONS: msg`You don't have permission to perform this action`,
  INTERFACE_SWITCH_UNAVAILABLE: msg`That view isn't available`,
  PRINCIPAL_ADMIN_EXISTS: msg`This company already has a principal administrator`,
  SUPER_ADMIN_ONLY: msg`This area is restricted to D'Anchiano super-admins`,
  OFFER_NOT_FOUND: msg`Offer not found`,
  APPLICANT_NOT_FOUND: msg`Applicant not found`,
  APPLICANT_ONLY: msg`This action is only available to professional accounts`,
  COMPANY_ONLY: msg`This action is only available to company accounts`,
  EMAIL_IN_USE: msg`This email has already been used`,
  EMAIL_REQUIRED: msg`An email address is required`,
  CONTACT_RATE_LIMITED: msg`You've sent us several messages already. Please try again later.`,
  SOCIAL_ID_IN_USE: msg`This social account is already linked to another account`,
  TOKEN_INVALID: msg`This link is invalid or has expired`,
  ORG_NOT_FOUND: msg`Organization not found`,
  TEAM_NOT_FOUND: msg`Team not found`,
  TEAM_ALREADY_EXISTS: msg`A team with this name already exists`,
  MEMBER_ALREADY_ON_TEAM: msg`This member is already on the team`,
  MEMBER_NOT_ON_TEAM: msg`This member is not on the team`,
  COMPANY_NOT_FOUND: msg`Company not found`,
  CUSTOMER_NOT_FOUND: msg`Customer not found`,
  INVALID_PRODUCT: msg`Invalid or unavailable product`,
  PAYMENT_UNAVAILABLE: msg`Payments are temporarily unavailable. Please try again.`,
  AGENT_UNAVAILABLE: msg`The assistant is not available right now`,
  AGENT_RATE_LIMITED: msg`You're sending messages too quickly. Wait a minute and try again.`,
  AGENT_BUDGET_EXCEEDED: msg`The assistant's usage limit has been reached. Try again later.`,
  AGENT_REQUEST_TOO_LARGE: msg`This conversation is too long. Start a new one to continue.`,
  AGENT_THREAD_NOT_FOUND: msg`This conversation is no longer available. Start a new one.`,
  AGENT_THREAD_STALE: msg`This conversation changed in another window. Reload it to continue.`,
  AGENT_THREAD_BUSY: msg`This conversation is still answering in another window.`,
  AGENT_RUN_LOST: msg`The answer was interrupted. Ask again.`,
  MCP_INVALID_REQUEST: msg`This connection request is not valid. Start it again from the AI app.`,
  MCP_COMPANY_NOT_ENABLED: msg`Your company has not allowed connecting external AI apps. Ask an administrator to turn it on.`,
  MCP_CONNECTION_NOT_FOUND: msg`That connection no longer exists`,
  AGENT_MESSAGE_NOT_FOUND: msg`That message is no longer available`,
  AGENT_TERMS_NOT_ACCEPTED: msg`Accept the assistant's terms to continue`,
  APP_NOT_FOUND: msg`Application not found`,
  APP_HAS_LINKED_ENTITIES: msg`This client still has candidates, companies or jobs linked. Unlink or delete them first.`,
  PROFILE_BLOCKED: msg`This profile isn't available`,
  PROFILE_NOT_DEFINED: msg`No profile selected`,
  TEST_NOT_PASSED: msg`The psychographic test isn't completed yet`,
  INVITATION_INVALID: msg`This invitation is invalid or has expired`,
  INVITATION_NOT_RESENDABLE: msg`This employee has no invitation to resend`,
  TEAMS_REEVALUATE_FORBIDDEN: msg`You don't have permission to re-evaluate this member`,
  DOMAIN_MISMATCH: msg`This request isn't allowed from this domain`,
}

/** The translation of a machine code from a non-tRPC surface (the assistant's stream), if it has one. */
export function errorCodeMessage(code: string): MessageDescriptor | undefined {
  return ERROR_TRANSLATIONS[code as ErrorCode]
}

// Wired into the query and mutation caches rather than a tRPC link, so the query path can be observer-gated.
// Browser only — SSR query errors go to the router error boundary, and `window` is undefined there.
function handleClientTrpcError(error: unknown): void {
  if (typeof window === 'undefined') return
  const err = error as { message?: string; data?: { code?: string } }
  const code = (err.message ?? '') as ErrorCode
  const descriptor = ERROR_TRANSLATIONS[code]
  const action = classifyTrpcErrorToast({ code, dataCode: err.data?.code, hasTranslation: descriptor != null })
  if (action === 'redirect-login') {
    // INVALID_CREDENTIALS is excluded so a failed login keeps its in-place message. resolveRedirectTarget unwraps
    // the current URL, so firing this while already on /login reuses the real destination.
    const target = resolveRedirectTarget(window.location.pathname + window.location.search)
    const query = target ? `?redirect=${encodeURIComponent(target)}` : ''
    window.location.href = withSdkParams(`${basename}/login${query}`)
    return
  }
  if (action === 'redirect-test') {
    // Guard against a loop while already on a /test route (those pages only call ungated test/auth endpoints).
    if (!window.location.pathname.includes('/test')) {
      window.location.href = withSdkParams(`${basename}${firstIncompleteTestPath()}`)
    }
    return
  }
  if (action === 'redirect-report') {
    // A self-registered professional reached a company/employee-only section → their own profile.
    if (!window.location.pathname.includes('/report')) {
      window.location.href = withSdkParams(`${basename}/report`)
    }
    return
  }
  if (action === 'redirect-company') {
    // The inverse: an employee in company view hit their own /report page → the company home.
    if (!window.location.pathname.includes('/jobs')) {
      window.location.href = withSdkParams(`${basename}/jobs`)
    }
    return
  }
  if (action === 'toast-generic') {
    // A 500's detail (raw SQL / stack) is redacted by the server, so show a generic toast.
    toast.error(i18n._(msg`Something went wrong. Please try again.`))
    return
  }
  // toast-translated → render the known code's translation; toast-raw → surface the raw message.
  toast.error(action === 'toast-translated' && descriptor ? i18n._(descriptor) : (err.message ?? ''))
}

/**
 * Terminating link for SSR: dispatches through `trpcSSRCallFn`, a plain direct call that runs the procedure
 * in-process against `appRouter` (no server-function RPC — see the profile notes in lib/trpc-server-fn.ts).
 * It is wrapped in `createServerOnlyFn`, so the start compiler erases its body AND the `server/*` imports from
 * the client bundle, leaving a throwing stub that is never reached: this link is only wired into the chain
 * when `isServer` (switch below).
 */
const ssrLink: TRPCLink<AppRouter> = () => {
  return ({ op }) => {
    return observable((observer) => {
      const type = op.type === 'subscription' ? 'query' : op.type
      trpcSSRCallFn({ path: op.path, input: op.input, type })
        .then((result) => {
          observer.next({ result: { type: 'data', data: result } })
          observer.complete()
        })
        .catch((err: unknown) => {
          observer.error(
            err instanceof TRPCClientError
              ? err
              : new TRPCClientError<AppRouter>(err instanceof Error ? err.message : String(err), {
                  cause: err instanceof Error ? err : undefined,
                }),
          )
        })
    })
  }
}

// Resolved once, lazily: `headers()` below runs on every tRPC call. `null` = resolved but unavailable.
let cachedTimezone: string | null | undefined
function resolveTimezone(): string | null {
  if (cachedTimezone === undefined) {
    try {
      cachedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null
    } catch {
      cachedTimezone = null
    }
  }
  return cachedTimezone
}

// ONE options object, so both links hit the same `/trpc` endpoint with the same auth/locale headers — only their
// batching and streaming behaviour differs.
const httpLinkOptions = {
  url: `${basename}/trpc`,
  headers() {
    const headers: HTTPHeaders = {}

    const { clientId: storeClientId, locale, userToken: storeUserToken } = useAppStore.getState().store
    const clientId = new URLSearchParams(location.search).get('clientId') ?? storeClientId
    // Fall back to the store (captured at load), so the token survives a navigation that drops it from the URL.
    const userToken = new URLSearchParams(location.search).get('token') ?? storeUserToken

    if (clientId) headers['Client-id'] = clientId
    if (userToken) headers['Authorization'] = `Bearer ${userToken}`
    // The slot the server checks before the `language` cookie; the store already resolved `?lang=` / lang / cookie.
    if (locale) headers['x-locale'] = locale

    if (typeof window !== 'undefined') {
      if (window.document.referrer) {
        const pathArray = window.document.referrer.split('/')
        const origin = `${pathArray[0]}//${pathArray[2]}`
        headers['Iframe-origin'] = origin
      } else {
        const pathArray = window.location.href.split('/')
        const origin = `${pathArray[0]}//${pathArray[2]}`
        headers['Iframe-origin'] = origin
      }

      // Wide-events correlation, so this request, the page render, any client error and the session timeline join one
      // trace (see wide-events/hono.ts). All best-effort; empty ids are omitted.
      Object.assign(headers, correlationHeaders())
      const tz = resolveTimezone()
      if (tz) headers['x-timezone'] = tz
      headers['x-viewport'] = `${String(window.innerWidth)}x${String(window.innerHeight)}`
      if (window.APP_VERSION) headers['x-app-version'] = window.APP_VERSION
    }

    return headers
  },
}

// Fetch a query imperatively WITHOUT publishing into the react-query cache (company registration writes the
// fresh user inside a view transition, so the auth-card → wizard box morphs deterministically).
export const trpcClient = createTRPCClient<AppRouter>({
  // The window-coupled `headers()` below would crash on the server, whose only consumers are the `/_app`
  // loaders and components — so it uses the in-process `ssrLink` exclusively.
  links: isServer
    ? [ssrLink]
    : [
        // Mutations set the auth session cookie. The streaming link holds the response connection open for batching, so
        // the post-login client navigation aborted it before the browser committed the Set-Cookie and the session was
        // silently dropped — route MUTATIONS through the non-streaming httpBatchLink. Queries keep the streaming one.
        splitLink({
          condition: (op) => op.type === 'mutation',
          true: httpBatchLink({
            ...httpLinkOptions,
            headers: ({ opList }) => ({ ...httpLinkOptions.headers(), ...flightHeaders(queryClient, opList) }),
            fetch: createFlightFetch(queryClient, timedFetch()),
          }),
          false: httpBatchStreamLink({ ...httpLinkOptions, fetch: timedFetch() }),
        }),
      ],
})

export const trpc: ReturnType<typeof createTRPCOptionsProxy<AppRouter>> = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
})

// The session query is read with suspense at the ROOT, so an entry of it that cannot fetch takes the whole
// app to the error boundary. Why an entry can end up without a queryFn: lib/session-query.ts (issue #108).
fetchableByDefault(queryClient, trpc.auth.getUser.queryOptions())

export type User = NonNullable<inferRouterOutputs<AppRouter>['auth']['getUser']>

/**
 * May this document read/write the origin's persisted query cache? False for a token-scoped or iframe-embedded
 * document, which authenticates as a different entity than the session that owns the blob. Read ONCE at module
 * scope: the SDK params can only enter on a document load, and a value that flipped later would make the
 * persister's behaviour depend on when a write happened to fire.
 */
export const OWNS_PERSISTED_CACHE =
  typeof window !== 'undefined' && documentOwnsPersistedCache(window.location.search, window.self === window.top)

// Gating the STORAGE and not the provider is deliberate: `router.tsx` keeps rendering PersistQueryClientProvider
// either way, so the React tree stays identical to the server's and nothing can mismatch.
const storagePersister = createAsyncStoragePersister({
  storage: OWNS_PERSISTED_CACHE ? window.localStorage : undefined,
  retry: removeOldestQuery,
})

// Dropped so this document's own copy wins the first render, then refetched once the tree is live.
const supersededByDocument = new Set<string>()

// Records WHICH session context the persisted blob belongs to (`persistedCacheIdentity`). Every identity
// transition but password login and logout lands on a document load that finds the previous context's blob.
export const PERSISTED_IDENTITY_KEY = 'dc-persisted-user-id'

// Read before this document's first effect restamps it for the session it renders.
const blobOwnerAtLoad = OWNS_PERSISTED_CACHE ? window.localStorage.getItem(PERSISTED_IDENTITY_KEY) : null

let blobOwnerChecked: Promise<void> | undefined

/**
 * Drop the blob when it belongs to another context than this document's session, BEFORE any of it is restored:
 * the document's own queries are already the new context's, so nothing painted has to be torn down
 * (docs/notes/app/query-persistence.md, "whose blob is this?"). An untagged blob predates the tag and is trusted.
 */
function discardBlobOfAnotherContext(): Promise<void> {
  blobOwnerChecked ??= (async () => {
    if (blobOwnerAtLoad === null) return
    const user = await queryClient.ensureQueryData(trpc.auth.getUser.queryOptions()).catch(() => undefined)
    if (user !== undefined && persistedCacheIdentity(user) === blobOwnerAtLoad) return
    await storagePersister.removeClient()
  })()
  return blobOwnerChecked
}

/**
 * Document-first: the persisted copy of any query THIS document carries is never restored, so React's first
 * render reads what the HTML says (docs/notes/app/query-persistence.md). Wrapped on the persister rather than at the
 * call site because `PersistQueryClientProvider` restores on mount too.
 */
export const persister: typeof storagePersister = {
  ...storagePersister,
  restoreClient: async () => {
    await discardBlobOfAnotherContext()
    const restored = await storagePersister.restoreClient()
    if (!restored) return restored
    const cache = queryClient.getQueryCache()
    const documentCarries = (hash: string) => cache.get(hash) !== undefined
    const superseded = supersededDocumentQueries(
      restored.clientState.queries,
      documentCarries,
      documentRenderedAt(cache.getAll()),
    )
    for (const hash of superseded) supersededByDocument.add(hash)
    return {
      ...restored,
      clientState: {
        ...restored.clientState,
        queries: withoutDocumentQueries(restored.clientState.queries, documentCarries).filter(isPersistable),
      },
    }
  },
}

// Bump when a query's cached shape changes incompatibly, or when a release must start from a clean cache.
export const PERSIST_BUSTER = 'v2'

// Shared by the eager `persistQueryClient` and the `PersistQueryClientProvider` in router.tsx, so both agree.
export const persistOptions = {
  persister,
  buster: PERSIST_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) => defaultShouldDehydrateQuery(query) && isPersistable(query),
  },
}

// The context whose data the IN-MEMORY cache holds; null for a cache that holds nobody's yet (or no longer).
let liveCacheIdentity: string | null = null

export const clearQueryClient = () => {
  queryClient.clear()
  liveCacheIdentity = null
  // Removed directly because the persister takes a second to sync. Only for the document that OWNS the blob: a
  // login or logout inside a token embed must not delete the surrounding owner session's cache.
  if (!OWNS_PERSISTED_CACHE) return
  window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE')
  // The tag goes WITH the blob it describes, so no claim about cleared data outlives it.
  window.localStorage.removeItem(PERSISTED_IDENTITY_KEY)
}

/**
 * Record that the live cache now serves `identity`, and say whether it was holding ANOTHER context's data: a
 * session that changed under a live document (a role granted mid-session), which only a clear corrects. A cleared
 * cache and a fresh document hold nobody's, so a login that clears before it navigates is never cleared twice.
 */
export function adoptCacheIdentity(identity: string): boolean {
  const heldAnother = liveCacheIdentity !== null && liveCacheIdentity !== identity
  liveCacheIdentity = identity
  return heldAnother
}

let restoreStarted = false

/**
 * Restore the persisted cache, exactly once, at the moment `router.tsx` chooses — which is the whole point:
 * `PersistQueryClientProvider` restores from an effect on mount, too late to decide what the first render
 * reads, and this module's import is far too early (docs/notes/app/query-persistence.md).
 */
export function restorePersistedCache(): void {
  if (restoreStarted) return
  restoreStarted = true
  const [, restored] = persistQueryClient({ queryClient, ...persistOptions })
  restored
    .then(() => {
      // Pay back what document-first cost this visitor, and nothing else: empty on a fresh document.
      if (supersededByDocument.size > 0) {
        void queryClient.invalidateQueries({ predicate: (query) => supersededByDocument.has(query.queryHash) })
      }
      // `persistQueryClient` only subscribes to LATER cache events, and the document's queries have settled by
      // now, so without this the blob is never written on an ordinary load.
      return persistQueryClientSave({ queryClient, ...persistOptions })
    })
    .catch(() => {
      // A cache we could not persist costs the next visit a warm start, nothing else.
    })
}
